/**
 * SyncEngine — incremental delta-fetch orchestrator.
 *
 * - Persists the React Query cache to IndexedDB (via idb-keyval) so the app
 *   boots with the last known snapshot instantly.
 * - Tracks a per-dashboard `lastSyncTs` (server timestamp of the last
 *   successful delta). Sends it to `GET /api/sync?since=...` and patches the
 *   React Query cache with the response.
 * - `syncNow` is called on login, window-focus, network-reconnect, and after
 *   mutations that need immediate reconciliation.
 * - `clearAll` wipes the persisted cache + sync metadata on logout.
 */

import { get, set, del, keys, createStore } from 'idb-keyval';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import api from './api';
import { QUERY_KEYS } from './TransactionService';

// Bump when the persisted schema shape changes (busts old caches on upgrade)
export const CACHE_VERSION = 'v1';

// Dedicated IndexedDB stores keep our data out of any app-level keys
const queryStore = createStore('budgetify-query-cache', 'queries');
const syncMetaStore = createStore('budgetify-sync-meta', 'meta');

const LAST_SYNC_PREFIX = 'lastSyncTs:';

// ── Persister (for PersistQueryClientProvider) ────────────────
const asyncStorage = {
  getItem: (key) => get(key, queryStore),
  setItem: (key, value) => set(key, value, queryStore),
  removeItem: (key) => del(key, queryStore),
};

export const queryPersister = createAsyncStoragePersister({
  storage: asyncStorage,
  key: `budgetify-rq-${CACHE_VERSION}`,
  throttleTime: 1000,
});

// ── Sync metadata (last sync timestamp per dashboard) ─────────
export async function getLastSync(dashboardId) {
  if (!dashboardId) return null;
  return (await get(LAST_SYNC_PREFIX + dashboardId, syncMetaStore)) || null;
}

export async function setLastSync(dashboardId, ts) {
  if (!dashboardId || !ts) return;
  await set(LAST_SYNC_PREFIX + dashboardId, ts, syncMetaStore);
}

// ── syncNow: ask the server for changes since last sync ───────
export async function syncNow(queryClient, dashboardId) {
  if (!dashboardId) return { ok: false, reason: 'no-dashboard' };

  const since = await getLastSync(dashboardId);
  const params = new URLSearchParams({ dashboard_id: dashboardId });
  if (since) params.append('since', since);

  let response;
  try {
    // Bootstrap call (no `since`) is the cold-cache path that fires when a
    // returning user opens the app. Give the (possibly sleeping) backend
    // up to 60s to wake. Incremental delta calls keep the default snappy
    // timeout — cached data is already on screen, we don't need to wait.
    response = await api.get(`/api/sync?${params.toString()}`, {
      timeout: since ? 15000 : 60000,
    });
  } catch (err) {
    // Offline / server down — surface but don't throw, let realtime cover
    console.warn('[SyncEngine] syncNow failed', err?.message || err);
    return { ok: false, reason: 'request-failed' };
  }

  const { server_time, tables = {} } = response.data || {};
  applyDelta(queryClient, dashboardId, tables, !since);
  if (server_time) await setLastSync(dashboardId, server_time);
  return { ok: true, server_time, bootstrap: !since };
}

// ── Apply a delta response to the React Query cache ───────────
function applyDelta(queryClient, dashboardId, tables, isBootstrap) {
  if (tables.transactions) {
    patchListCache(
      queryClient,
      QUERY_KEYS.allTransactions(dashboardId),
      tables.transactions,
      isBootstrap,
      (tx) => tx.id,
      // Keep newest first (the list is rendered in this order)
      (a, b) => {
        const da = a.transaction_date || '';
        const db = b.transaction_date || '';
        if (da !== db) return da < db ? 1 : -1;
        const ca = a.created_at || '';
        const cb = b.created_at || '';
        return ca < cb ? 1 : -1;
      },
    );
  }

  if (tables.accounts) {
    patchListCache(
      queryClient,
      QUERY_KEYS.accounts(dashboardId),
      tables.accounts,
      isBootstrap,
      (a) => a.id,
      // Keep accounts sorted by user-defined display_order so reorders
      // applied via /api/accounts/reorder propagate consistently to every
      // consumer (Add Transaction picker, dashboards, manager modal).
      (a, b) => {
        const oa = a.display_order ?? 0;
        const ob = b.display_order ?? 0;
        if (oa !== ob) return oa - ob;
        return (a.created_at || '').localeCompare(b.created_at || '');
      },
    );
  }

  // Future tables land in dedicated cache keys; invalidation is a safe
  // fallback so any screen reading those keys refetches on demand.
  for (const name of Object.keys(tables)) {
    if (name === 'transactions' || name === 'accounts') continue;
    queryClient.invalidateQueries({ queryKey: [name, dashboardId] });
  }

  // Summary stats are derived — always invalidate so they recompute.
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(dashboardId) });
}

function patchListCache(queryClient, key, delta, isBootstrap, getId, sortFn) {
  const upserted = delta.upserted || [];
  const deleted = new Set((delta.deleted || []).map(String));

  queryClient.setQueryData(key, (old = []) => {
    // Bootstrap: server returned the full list; replace.
    if (isBootstrap) {
      const list = [...upserted];
      if (sortFn) list.sort(sortFn);
      return list;
    }

    const byId = new Map();
    for (const row of old) byId.set(String(getId(row)), row);
    for (const row of upserted) byId.set(String(getId(row)), row);
    for (const id of deleted) byId.delete(id);

    const merged = Array.from(byId.values());
    if (sortFn) merged.sort(sortFn);
    return merged;
  });
}

// ── clearAll: wipe cache + sync metadata (logout) ─────────────
export async function clearAll() {
  try {
    const queryKeys = await keys(queryStore);
    await Promise.all(queryKeys.map((k) => del(k, queryStore)));
    const metaKeys = await keys(syncMetaStore);
    await Promise.all(metaKeys.map((k) => del(k, syncMetaStore)));
  } catch (err) {
    console.warn('[SyncEngine] clearAll failed', err);
  }
}
